import { loadImage } from '@/utils/fileDownload'

export type ChartExportReport = {
  title: string
  generatedAt: Date
  xAxisGroup: string
  yAxisGroup: string
  categories: string[]
  chartImageDataUrl: string
}

const REPORT_WIDTH = 1200
const REPORT_PADDING = 48
const REPORT_TEXT_WIDTH = REPORT_WIDTH - REPORT_PADDING * 2

const formatGeneratedAt = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)

const drawWrappedText = (
  canvasContext: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) => {
  const words = text.split(' ')
  let line = ''
  let currentY = y

  for (const word of words) {
    const nextLine = line ? `${line} ${word}` : word
    if (canvasContext.measureText(nextLine).width > maxWidth && line) {
      canvasContext.fillText(line, x, currentY)
      line = word
      currentY += lineHeight
    } else {
      line = nextLine
    }
  }

  if (line) {
    canvasContext.fillText(line, x, currentY)
  }

  return currentY + lineHeight
}

export const buildReportCanvas = async (report: ChartExportReport, imageType: 'png' | 'jpg') => {
  const chartImage = await loadImage(report.chartImageDataUrl)
  const chartWidth = REPORT_TEXT_WIDTH
  const chartHeight = Math.round((chartImage.height / chartImage.width) * chartWidth)
  const canvas = document.createElement('canvas')
  const metadataHeight = 360
  canvas.width = REPORT_WIDTH
  canvas.height = metadataHeight + chartHeight + REPORT_PADDING

  const canvasContext = canvas.getContext('2d')
  if (!canvasContext) return null

  canvasContext.fillStyle = '#ffffff'
  canvasContext.fillRect(0, 0, canvas.width, canvas.height)
  canvasContext.fillStyle = '#111111'
  canvasContext.font = '700 28px Arial'
  canvasContext.fillText(report.title, REPORT_PADDING, 54)

  canvasContext.font = '400 15px Arial'
  canvasContext.fillText(
    `Generated date/time: ${formatGeneratedAt(report.generatedAt)}`,
    REPORT_PADDING,
    88
  )

  canvasContext.font = '700 16px Arial'
  canvasContext.fillText('Applied filters:', REPORT_PADDING, 124)

  canvasContext.font = '400 15px Arial'
  canvasContext.fillText(`X-axis group: ${report.xAxisGroup}`, REPORT_PADDING + 18, 152)
  canvasContext.fillText(`Y-axis group: ${report.yAxisGroup}`, REPORT_PADDING + 18, 178)
  const categories = report.categories.length ? report.categories.join(', ') : 'None'
  const chartTitleY =
    drawWrappedText(
      canvasContext,
      `Categories: ${categories}`,
      REPORT_PADDING + 18,
      204,
      REPORT_TEXT_WIDTH - 18,
      22
    ) + 20

  canvasContext.font = '700 16px Arial'
  canvasContext.fillText('Chart image', REPORT_PADDING, chartTitleY)
  const chartY = chartTitleY + 24

  canvasContext.drawImage(chartImage, REPORT_PADDING, chartY, chartWidth, chartHeight)

  return canvas.toDataURL(imageType === 'jpg' ? 'image/jpeg' : 'image/png', 0.95)
}
